import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDraftInvoices,
  fetchIssuedInvoices,
} from "../../redux/actions/InvoiceActions";
import {
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import SaveAsPaidModal from "./SaveAsPaidModal";
import SendReceiptModal from "./SendRecieptModal";
import axios from "axios";
import { API_URI } from "../../constants/GlobalConstants";
import { ListDealerAction } from "../../redux/actions/DealerAction";
import IssuedInvoiceTable from "./IssuedInvoiceTable";
import DraftInvoiceTable from "./DraftInvoiceTable";

function InvoiceListingComp() {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;

  // Redux state
  const { invoices, loading } = useSelector((state) => state.invoices);
  const { draft, issued } = invoices;
  const { draft: draftLoading, issued: issuedLoading } = loading;
  const { dealer } = useSelector((state) => state.listDealer);

  // Local state
  const [draftPage, setDraftPage] = useState(0);
  const [issuedPage, setIssuedPage] = useState(0);
  const [draftRowsPerPage, setDraftRowsPerPage] = useState(10);
  const [issuedRowsPerPage, setIssuedRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openReceiptModal, setOpenReceiptModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [selectedIssuedDealer, setSelectedIssuedDealer] = useState("");
  const [selectedDraftDealer, setSelectedDraftDealer] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  
  // Sorting state
  const [issuedSortBy, setIssuedSortBy] = useState("invoiceDate");
  const [issuedSortOrder, setIssuedSortOrder] = useState("desc");
  const [draftSortBy, setDraftSortBy] = useState("invoiceDate");
  const [draftSortOrder, setDraftSortOrder] = useState("desc");

  // Calculate total remaining balance for selected invoices
  const totalRemainingBalance = selectedRows.reduce((sum, id) => {
    const invoice = issued.data.find((inv) => inv._id === id);
    return invoice ? sum + (invoice.remainingBalance || invoice.total) : sum;
  }, 0);

  // Check if selected invoices have mixed types
  const selectedInvoiceTypes = [
    ...new Set(
      selectedRows
        .map((id) => issued.data.find((inv) => inv._id === id)?.type)
        .filter(Boolean)
    ),
  ];
  const hasMixedInvoiceTypes = selectedInvoiceTypes.length > 1;
  const allPaid = selectedRows.every(
    (id) => issued.data.find((inv) => inv._id === id)?.status === "Paid"
  );

  // Effects to fetch data with sorting
  useEffect(() => {
    dispatch(
      fetchIssuedInvoices({
        dealerId: selectedIssuedDealer,
        page: issuedPage + 1,
        limit: issuedRowsPerPage,
        sortBy: issuedSortBy,
        sortOrder: issuedSortOrder,
      })
    );
  }, [dispatch, selectedIssuedDealer, issuedPage, issuedRowsPerPage, issuedSortBy, issuedSortOrder]);

  useEffect(() => {
    dispatch(
      fetchDraftInvoices({
        dealerId: selectedDraftDealer,
        page: draftPage + 1,
        limit: draftRowsPerPage,
        sortBy: draftSortBy,
        sortOrder: draftSortOrder,
      })
    );
  }, [dispatch, selectedDraftDealer, draftPage, draftRowsPerPage, draftSortBy, draftSortOrder]);

  useEffect(() => {
    dispatch(ListDealerAction(userInfo.token));
  }, [dispatch, userInfo.token]);

  // Handlers
  const handleDraftPageChange = (event, newPage) => setDraftPage(newPage);
  const handleIssuedPageChange = (event, newPage) => setIssuedPage(newPage);
  
  const handleDraftRowsPerPageChange = (event) => {
    setDraftRowsPerPage(parseInt(event.target.value, 10));
    setDraftPage(0);
  };
  
  const handleIssuedRowsPerPageChange = (event) => {
    setIssuedRowsPerPage(parseInt(event.target.value, 10));
    setIssuedPage(0);
  };
  
  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };
  
  const handleSelectAll = (data) => {
    if (selectedRows.length === data.length) setSelectedRows([]);
    else setSelectedRows(data.map((row) => row._id));
  };

  // Sorting handlers
  const handleIssuedSortChange = (field) => {
    if (issuedSortBy === field) {
      // Toggle sort order if clicking the same field
      setIssuedSortOrder(issuedSortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with default desc order
      setIssuedSortBy(field);
      setIssuedSortOrder("desc");
    }
  };

  const handleDraftSortChange = (field) => {
    if (draftSortBy === field) {
      setDraftSortOrder(draftSortOrder === "asc" ? "desc" : "asc");
    } else {
      setDraftSortBy(field);
      setDraftSortOrder("desc");
    }
  };

  const handleSaveAsPaid = async (modeOfPayment, paymentAmount) => {
    setLoadingModal(true);
    try {
      if (hasMixedInvoiceTypes) {
        throw new Error("Cannot process Custom and Order-Based invoices together");
      }

      const payload = {
        id: selectedRows,
        status: "Paid",
        modeOfPayment,
      };
      if (paymentAmount) {
        payload.paymentAmount = parseFloat(paymentAmount);
      }
      await axios.post(`${API_URI}/api/invoices/update-invoice-status`, payload, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setSnackbar({
        open: true,
        message: paymentAmount
          ? "Partial payment applied successfully"
          : "Invoices marked as paid successfully",
        severity: "success",
      });
      setTimeout(() => {
        setLoadingModal(false);
        setOpenModal(false);
        setOpenReceiptModal(true);
      }, 1000);
      dispatch(
        fetchIssuedInvoices({
          dealerId: selectedIssuedDealer,
          page: issuedPage + 1,
          limit: issuedRowsPerPage,
          sortBy: issuedSortBy,
          sortOrder: issuedSortOrder,
        })
      );
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to process payment",
        severity: "error",
      });
      setLoadingModal(false);
    }
  };

  const handleSendReceipt = async () => {
    setLoadingReceipt(true);
    try {
      const response = await axios.post(
        `${API_URI}/api/invoices/send-receipt`,
        { invoiceIds: selectedRows },
        {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        }
      );
      if (response.status === 207) {
        setSnackbar({
          open: true,
          message: "Some receipts failed to send",
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Receipts sent successfully",
          severity: "success",
        });
      }
      setSelectedRows([]);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to send receipts",
        severity: "error",
      });
    }
    setLoadingReceipt(false);
    setOpenReceiptModal(false);
    setActionAnchorEl(null);
  };

  const handleIssuedDealerChange = (event) => {
    setSelectedIssuedDealer(event.target.value);
  };

  const handleDraftDealerChange = (event) => {
    setSelectedDraftDealer(event.target.value);
  };

  return (
    <div className="container-fluid py-4">
      {/* Issued Invoices Table */}
      <IssuedInvoiceTable
        issued={issued}
        issuedLoading={issuedLoading}
        selectedRows={selectedRows}
        handleSelectRow={handleSelectRow}
        handleSelectAll={handleSelectAll}
        issuedPage={issuedPage}
        issuedRowsPerPage={issuedRowsPerPage}
        handleIssuedPageChange={handleIssuedPageChange}
        handleIssuedRowsPerPageChange={handleIssuedRowsPerPageChange}
        hasMixedInvoiceTypes={hasMixedInvoiceTypes}
        dealer={dealer}
        selectedIssuedDealer={selectedIssuedDealer}
        handleIssuedDealerChange={handleIssuedDealerChange}
        actionAnchorEl={actionAnchorEl}
        setActionAnchorEl={setActionAnchorEl}
        allPaid={allPaid}
        setOpenModal={setOpenModal}
        setOpenReceiptModal={setOpenReceiptModal}
        sortBy={issuedSortBy}
        sortOrder={issuedSortOrder}
        handleSortChange={handleIssuedSortChange}
      />

      {/* Draft Invoices Table */}
      <DraftInvoiceTable
        draft={draft}
        draftLoading={draftLoading}
        draftPage={draftPage}
        draftRowsPerPage={draftRowsPerPage}
        handleDraftPageChange={handleDraftPageChange}
        handleDraftRowsPerPageChange={handleDraftRowsPerPageChange}
        dealer={dealer}
        selectedDraftDealer={selectedDraftDealer}
        handleDraftDealerChange={handleDraftDealerChange}
        sortBy={draftSortBy}
        sortOrder={draftSortOrder}
        handleSortChange={handleDraftSortChange}
      />

      {/* Modals and Snackbar */}
      <SaveAsPaidModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveAsPaid}
        loading={loadingModal}
        totalAmount={totalRemainingBalance}
        invoiceCount={selectedRows.length}
      />
      <SendReceiptModal
        open={openReceiptModal}
        onClose={() => {
          setOpenReceiptModal(false);
          setSelectedRows([]);
        }}
        onSend={handleSendReceipt}
        loading={loadingReceipt}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default InvoiceListingComp;